"use client";

// =========================================================================
// Debounced record search behind the palette.
//
// Results are filtered SERVER-SIDE against fields cmdk cannot see (an email
// address, a company domain), which is why the palette runs
// `shouldFilter={false}` and carries a validate-command-shouldfilter-allow.
// Split out of CommandPalette.tsx (module-size).
// =========================================================================

import { useEffect, useRef, useState } from "react";
import type { SearchResult } from "@/lib/hooks/algolia-search/types";

/** Debounce before firing the record search, so typing doesn't spam the API. */
export const SEARCH_DEBOUNCE_MS = 220;

/** Record results shown at once; the full-page search shows everything. */
export const MAX_RECORD_RESULTS = 6;

/** Two characters is the floor where a record search is worth a round trip. */
export const MIN_RECORD_QUERY_LENGTH = 2;

export function usePaletteRecordSearch(
  open: boolean,
  trimmedQuery: string,
  onSearchRecords: (query: string) => Promise<SearchResult[]>,
): { records: SearchResult[]; isSearching: boolean } {
  const [records, setRecords] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Identifies the newest search so a slow earlier response can't overwrite a
  // newer one (classic out-of-order async results bug).
  const searchTokenRef = useRef(0);

  // Reset transient state whenever the palette closes, so reopening never shows
  // the previous query's results for a frame.
  useEffect(() => {
    if (open) return;
    setRecords([]);
    setIsSearching(false);
    searchTokenRef.current += 1;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (trimmedQuery.length < MIN_RECORD_QUERY_LENGTH) {
      setRecords([]);
      setIsSearching(false);
      searchTokenRef.current += 1;
      return;
    }

    const token = ++searchTokenRef.current;
    setIsSearching(true);

    const timer = setTimeout(() => {
      void onSearchRecords(trimmedQuery)
        .then((results) => {
          if (token !== searchTokenRef.current) return;
          setRecords(results.slice(0, MAX_RECORD_RESULTS));
        })
        .catch(() => {
          // A failed record search must not blank the palette: navigation and
          // create commands still work, which is most of its value.
          if (token !== searchTokenRef.current) return;
          setRecords([]);
        })
        .finally(() => {
          if (token !== searchTokenRef.current) return;
          setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, trimmedQuery, onSearchRecords]);

  return { records, isSearching };
}
